import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { useSearchParams } from 'react-router-dom';
import myShopClient from './nearlive';
import { listDeliveryCategories, listInventory } from './stockSearch';

export async function batchGetStock(barcodes, shopId = '') {
	const client = await myShopClient();
	const endpoint = `/api/inventory/${shopId ? `${shopId}/` : ''}batch`;
	const response = await client.post(endpoint, { barcodes });
	return response.data;
}

export async function getOrganisationLevelStock(barcode) {
	const client = await myShopClient();
	const endpoint = `/api/inventory/organisation-level/${barcode}`;
	const response = await client.get(endpoint);
	return response.data;
}

/**
 * this returns product data for a barcode
 * @param {String} barcode - The barcode being used to fetch data
 * @param {String} shopId - The shop id
 * @returns {Promise<{barcode:String, title:String, description:String, brand:String, images:String[]}|null>}
 */
export async function getStock(barcode, shopId) {
	const client = await myShopClient();
	const response = (await client.get(`/api/inventory/${shopId}/${barcode}`)).data;
	return response;
}

/**
 * Lists stock records for the specified shop.
 * @param {String} shopId - The ID of the shop for which to fetch stock records.
 * @param {Object} params - Optional query parameters to filter the stock records.
 * @returns {Promise<any[]>} - A promise that resolves to an array of stock records.
 */
export async function listStockRecords(shopId, params = {}) {
	const client = await myShopClient();
	const response = (await client.get(`/api/inventory/${shopId}`, { params })).data;
	return response;
}

/**
 * Get the stock information for all shops in the org that have a specific barcode in stock.
 *
 * @param {String} barcode
 * @returns {Promise<{shopId: String, quantity: Number, price: Number, currency: String}[]>}
 */
export async function getOrganisationStockStatus(barcode) {
	const client = await myShopClient();
	const response = (await client.get(`/api/inventory/organisation/${barcode}`)).data;
	return response;
}

export async function getInventoryStatus(shopId, channel) {
	const client = await myShopClient();
	const response = (await client.get(`/api/inventory/${shopId}/inventory-status?channel=${channel}`)).data;
	return response;
}

export async function getFeedQualityStats(shopId, channel) {
	const client = await myShopClient();
	const searchParams = new URLSearchParams({ channel });
	const response = (await client.get(`/api/inventory/${shopId}/feed-quality-stats${channel ? '?' + searchParams.toString() : ''}`)).data;
	return response;
}

/**
 * Aggregated feed-quality-stats across every shop the user has access to.
 * Use this on chain-view surfaces instead of looping `getFeedQualityStats` —
 * it collapses a 36-store fan-out into a single HTTP call (and a single
 * Lambda cold-start in dev).
 *
 * @param {string} [channel] - optional channel filter (e.g. 'google'). Falls
 *   back to the org-wide / `nearst` baseline when omitted.
 */
export async function getOrgFeedQualityStats(channel) {
	const client = await myShopClient();
	const path = channel ? `/api/inventory/feed-quality-stats?channel=${encodeURIComponent(channel)}` : `/api/inventory/feed-quality-stats`;
	const response = (await client.get(path)).data;
	return response;
}

export function useInventoryStatus(shopId, channel = 'nearst') {
	const key = `${shopId}-issues-${channel}`;
	return useSWR(key, () => getInventoryStatus(shopId, channel));
}

export async function exportInventoryIssues(shopId, issueType) {
	const client = await myShopClient();
	const response = (await client.post(`/api/inventory/${shopId}/inventory-issues/export/${issueType}`)).data;
	return response;
}

export async function exportInventory(shopId, query = {}) {
	const client = await myShopClient();
	const endpoint = shopId ? `/api/inventory/${shopId}/export` : '/api/inventory/export';
	const response = (await client.post(endpoint, query)).data;
	return response;
}

/**
 * Edit one or more product data attributes for a given barcode.
 * Works both for specific shops and across the organisation (if no `shopId` is defined).
 *
 * @param {string} barcode
 * @param {string} shopId
 * @param {object} modification
 */
export async function customiseProduct(barcode, shopId, modification) {
	const client = await myShopClient();
	const response = (await client.put(`/api/inventory/${shopId || 'organisation'}/${barcode}`, modification)).data;
	return response;
}

let autocompleteDeliveryCategories = null;

/**
 * Get a list of previously used delivery categories for a shop.
 *
 * @param {string} shopId
 * @returns {Promise<{value: string, label: string;}[]>}
 */
export async function getAutocompleteDeliveryCategories(organisationId, shopId = '') {
	if (!autocompleteDeliveryCategories) {
		autocompleteDeliveryCategories = await listDeliveryCategories({ organisationId, shopId });
	}

	return autocompleteDeliveryCategories;
}

/**
 * Get lists of previously used delivery categories for a shop, separated by aisle and shelf.
 *
 * @typedef {{value: string, label: string}[]} options
 *
 * @param {string} shopId
 * @returns {Promise<{ aisleOptions: options, shelfOptions: options }>}
 */
export async function getMenuCategoryOptions(shopId, aisle = '') {
	const client = await myShopClient();
	const menuCategoryOptions = (await client.get(`/api/${shopId}/menu-category-options?aisle=${aisle}`)).data;

	return menuCategoryOptions;
}

/**
 * Hide or unhide a list of barcodes.
 *
 * Updates the global `enabled` flag for the supplied stock rows, or channel-specific visibility when
 * `channels` is provided.
 *
 * @param {String} shopId
 * @param {String[]} barcodes
 * @param {Boolean} enabled
 * @param {Object} [channels] - Optional channel flags, e.g. { google: false, deliveroo: false }
 * @returns {Promise<{barcode:String, failures:String[]}[]>}
 */
export async function hideProducts(shopId, barcodes, enabled, channels) {
	const client = await myShopClient();

	const endpoint = `/api/inventory/hide${shopId ? `/${shopId}` : ''}`;
	const body = channels ? { barcodes, channels } : { barcodes, enabled };
	const result = await client.put(endpoint, body);

	return result.data;
}

export function useStock(barcode, shopId, config) {
	const key = `stock_data-${shopId}-${barcode}`;
	const { data, error } = useSWR(key, () => (shopId ? getStock(barcode, shopId) : getOrganisationLevelStock(barcode)), config);
	const isLoading = !data && !error;
	const isEmpty = data && !data.length;
	const resultSet = data && data.length ? data[0] : data;
	return { data: resultSet, error, isLoading, isEmpty };
}

export const availabilityFilters = [
	{
		value: 'in_stock:=true',
		checked: true,
		label: 'In stock'
	},
	{
		value: 'in_stock:=false',
		checked: false,
		label: 'Out of stock'
	}
];

export const statusFilters = [
	{
		value: 'enabled_i:=true && has_errors:!=true',
		checked: false,
		label: 'Active'
	},
	{
		value: 'productSource_i:=myshop',
		checked: false,
		label: 'Edited'
	},
	{
		value: 'enabled_i:=false',
		checked: false,
		label: 'Hidden'
	}
];

export const defaultFilters = [...availabilityFilters, ...statusFilters].reduce((acc, filter) => {
	return {
		...acc,
		[filter.value]: filter.checked
	};
}, {});

const jsonFiltersToComma = (filters) =>
	Object.entries(filters)
		.filter(([, value]) => Boolean(value))
		.map(([key]) => key)
		.join(',');

const commaFiltersToJson = (filters) =>
	Object.fromEntries(
		filters
			.split(',')
			.filter(Boolean)
			.map((key) => [key, true])
	);

/**
 * @typedef {{ barcode: string, title: string, price: number, quantity_i: number, in_stock: boolean, issues: string[]}} inventory
 */

/**
 *
 * @param {string} organisationId
 * @param {string} [shopId]
 * @returns {{
 * 	inventory: inventory[],
 * 	error: string,
 * 	isLoading: boolean,
 * 	isValidating: boolean,
 * 	isEmpty: boolean,
 * 	totalPages: number,
 * 	changePage: (number) => void,
 * 	page: number,
 * 	searchInput: string,
 * 	setSearchInput: (string) => void,
 * 	filters: {},
 * 	setFilters: ({}) => void,
 * 	applyFilters: () => void,
 *  optimisticUpdate: (cacheUpdater: (object[]), handler: () => Promise) => void
 *  mutate: () => void
 * }}
 */
export function useInventory(organisationId, shopId = '') {
	let [searchParams, setSearchParams] = useSearchParams();

	const INITIAL_PAGE = 1;
	const [totalPages, setTotalPages] = useState(INITIAL_PAGE);

	const handleParamChange = (key, value) => {
		const existingParams = searchParams;
		existingParams.set(key, value);
		setSearchParams(existingParams, { replace: true });
	};

	const searchInput = searchParams.get('searchInput') ? searchParams.get('searchInput') : '';
	const setSearchInput = (input) => {
		handleParamChange('searchInput', input);
		setPage(INITIAL_PAGE);
	};

	const filters = searchParams.has('filters') ? commaFiltersToJson(searchParams.get('filters')) : defaultFilters;
	const setFilters = (filters) => {
		// only need to keep the truthy filters for functionality - otherwise a long URL!
		const simpleFilters = jsonFiltersToComma(filters);
		handleParamChange('filters', simpleFilters);
		setPage(INITIAL_PAGE);
	};

	const applyFilters = (newFilters) => {
		setFilters(newFilters);
		mutate();
	};

	const page = searchParams.get('page') ? parseInt(searchParams.get('page')) : INITIAL_PAGE;
	const setPage = (page) => {
		handleParamChange('page', page.toString());
	};

	const key = [organisationId, shopId, page, searchInput, JSON.stringify(filters)].filter(Boolean).join('_');
	const { data, error, isLoading, isValidating, mutate } = useSWR(
		key,
		() => listInventory({ organisationId, shopId, page, query: searchInput, filters }),
		{
			dedupingInterval: 1000 * 60 * 1,
			focusThrottleInterval: 1000 * 60 * 1,
			revalidateIfStale: false,
			revalidateOnFocus: false
		}
	);

	const changePage = (page) => {
		if (page < 0 || page > totalPages) {
			throw Error('page out of bounds error');
		}
		setPage(page);
	};

	const isEmpty = data && !data?.inventory?.length;

	useEffect(() => {
		setTotalPages(data?.numOfPages || 0);
	}, [data?.numOfPages]);

	const optimisticUpdate = (cacheUpdater, handler) => {
		const optimisticData = {
			...data,
			inventory: cacheUpdater(data?.inventory)
		};
		mutate(
			async () => {
				await handler();
				return optimisticData;
			},
			{ optimisticData }
		);
	};

	return {
		inventory: data?.inventory,
		error,
		isLoading: isLoading,
		isValidating,
		isEmpty,
		totalPages,
		changePage,
		page,
		searchInput,
		setSearchInput,
		filters,
		setFilters,
		applyFilters,
		optimisticUpdate,
		mutate
	};
}
